// Décoration AUTOMATIQUE des niveaux de base (demande Claude-Alain 20/07 : « ajoute des déco aux
// niveaux de base »). 100 % COSMÉTIQUE : génère une couche `decor` DÉTERMINISTE à partir du terrain,
// SANS jamais toucher `rows`/slug/config -> le gameplay et les CHRONOS/RECORDS restent intacts (le
// decor n'entre pas dans la simulation, cf. render). Appliqué au RUNTIME (le catalogue et les .txt
// restent inchangés : le test de synchro levels-catalog reste vert). Placement : décos de SOL posées
// au-dessus d'un solide, décos MURALES (torches...) accrochées en hauteur, choisies par biome, espacées
// et JAMAIS collées à un élément de jeu (or, objets, spawns, sortie, échelle, corde, moine, téléporteur).
import { DECOS, BIG_DECOS, type BigDecoKind } from '../shared/decor'
import type { WorldKey } from '../shared/worlds'

const SOLID = new Set(['#', '=', 'x', 'I', 's'])                                   // sols/murs sur lesquels poser une déco
const KEEP_CLEAR = new Set(['$', 'b', 'j', 'z', 'E', '1', '2', '3', 'H', '-', 'm', 'T', 't', 'L', '/', '\\', 'D']) // éléments à ne pas encombrer

// Décos de SOL (posées) et MURALES (accrochées), par biome. Chars = ceux de DECOS (shared/decor.ts).
const FLOOR: Record<WorldKey, readonly string[]> = {
  classique: ['o', 'p', '4', '0', '7', 'K', '5'], // tonneau, plante, buisson, rocher, pierres, crâne, LAMPADAIRE (halo chaud)
  prairie: ['e', 'g', '4', 'p', '6', '0'],     // fleurs, herbe, buisson, plante, souche, rocher
  grotte: ['q', 'C', '?', '7', '0'],           // champignon, cristal, stalagmite, pierres, rocher
  desert: ['a', '0', 'K', '7'],                // cactus, rocher, crâne, pierres
  neige: ['8', '0', '7'],                      // bonhomme, rocher, pierres
  usine: ['o', 'h', '7'],                      // tonneau, chaîne, pierres
  abysses: ['[', '0', '7'],                    // corail, rocher, pierres
  manoir: ['|', 'K', '7', '0'],                // tombe, crâne, pierres, rocher
}
const WALL: Record<WorldKey, readonly string[]> = {
  classique: ['f', 'B'],                       // torche, bannière (lanterne déplacée au SOL = lampadaire éclairant)
  prairie: ['f', '5'],
  grotte: ['f', 'l', 'C'],                     // torche, lierre, cristal
  desert: ['f', '5'],
  neige: ['f', '5'],
  usine: ['Y', 'h', 'f'],                      // valve, chaîne, torche
  abysses: ['f', 'l'],
  manoir: ['i', '5', 'B'],                     // bougie, lanterne, bannière
}
// filtre défensif : ne garder que les chars réellement déclarés dans DECOS (évite un sprite fantôme)
const VALID = new Set(Object.values(DECOS).map((d) => d.ch))
const pick = (pal: readonly string[], k: number): string => { const v = pal.filter((c) => VALID.has(c)); return v.length ? v[k % v.length]! : '.' }

// GRAND DÉCO (point focal) posé auto par biome : arbre en prairie/neige, campement au désert, grue à
// l'usine, squelette de baleine aux abysses. Biomes absents = pas de grande pièce auto (ville/grotte/
// manoir : le fond premium + les pièces placées à la main suffisent). Purement cosmétique (couche décor).
const BIG_BY_BIOME: Partial<Record<WorldKey, BigDecoKind>> = { prairie: 'arbre', neige: 'arbre', desert: 'campement', usine: 'grue', abysses: 'baleine' }

const hash = (r: number, c: number, s: number): number => {
  let x = ((r * 73856093) ^ (c * 19349663) ^ (s * 83492791)) >>> 0
  x = (x ^ (x >>> 13)) >>> 0
  return (x ^ (x >>> 16)) >>> 0
}

/** Couche `decor` cosmétique déterministe pour un niveau (terrain `rows`, biome `world`). */
export function autoDecor(rows: readonly string[], world: WorldKey): string {
  const H = rows.length, W = Math.max(...rows.map((r) => r.length))
  const at = (r: number, c: number): string => rows[r]?.[c] ?? '.'
  const solid = (r: number, c: number): boolean => SOLID.has(at(r, c))
  const grid: string[][] = Array.from({ length: H }, () => Array.from({ length: W }, () => '.'))
  const near = (r: number, c: number): boolean => {              // un élément de jeu à proximité (8-connexité) ?
    for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) if (KEEP_CLEAR.has(at(r + dr, c + dc))) return true
    return false
  }
  const floor = FLOOR[world], wall = WALL[world]
  for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) {
    if (at(r, c) !== '.' || near(r, c)) continue
    if ((grid[r - 1]?.[c] ?? '.') !== '.') continue              // jamais empilé verticalement
    const l1 = (grid[r]?.[c - 1] ?? '.') !== '.', l2 = (grid[r]?.[c - 2] ?? '.') !== '.'
    if (l1 && l2) continue                                       // max 2 décos côte à côte (petits amas, pas un mur de décos)
    if (solid(r + 1, c) && !solid(r, c)) {                       // SOL : posée sur un solide
      // densité plus forte (plein la vue) ; une case déjà décorée à gauche prolonge parfois l'amas
      if (hash(r, c, 1) % 3 === 0 || (l1 && hash(r, c, 5) % 2 === 0)) grid[r]![c] = pick(floor, hash(r, c, 2))
    } else if ((solid(r, c - 1) || solid(r, c + 1)) && !solid(r + 1, c) && r > 1 && r < H - 3) { // MUR : en hauteur, pas au sol
      if (hash(r, c, 3) % 6 === 0) grid[r]![c] = pick(wall, hash(r, c, 4))
    }
  }
  // GRAND DÉCO : au plus 1 par niveau, ancré sur un sol, EMPRISE (w×h) entièrement libre — aucun
  // terrain ni élément de jeu masqué (lisibilité intacte, chronos/records inchangés car cosmétique).
  const bigKind = BIG_BY_BIOME[world]
  if (bigKind) {
    const m = BIG_DECOS[bigKind]
    const cands: [number, number][] = []
    for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) {
      if (solid(r, c) || !solid(r + 1, c)) continue           // ancre = case vide posée sur un sol
      const c0 = c - m.ax, r0 = r - m.ay
      if (c0 < 1 || r0 < 0 || c0 + m.w > W - 1) continue       // emprise dans les bornes (marge latérale 1)
      let ok = true
      for (let rr = r0; rr < r0 + m.h && ok; rr++) for (let cc = c0; cc < c0 + m.w && ok; cc++) {
        if (at(rr, cc) !== '.' || (grid[rr]?.[cc] ?? '.') !== '.') ok = false // emprise ENTIÈREMENT vide (terrain + déco) : rien masqué. Éléments ADJACENTS tolérés (pas de chevauchement)
      }
      if (ok) cands.push([r, c])
    }
    if (cands.length) {
      const [ar, ac] = cands[hash(H, W, cands.length) % cands.length]!
      for (let rr = ar - m.ay; rr < ar - m.ay + m.h; rr++) for (let cc = ac - m.ax; cc < ac - m.ax + m.w; cc++) if (grid[rr]) grid[rr]![cc] = '.' // efface les petites décos sous l'emprise
      grid[ar]![ac] = m.ch
    }
  }
  return grid.map((row) => row.join('')).join('\n')
}
